import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useQuery } from "@tanstack/react-query";
import {FaEraser, FaPen, FaPrint, FaSearch} from "react-icons/fa";
import moment from "moment";
import {useParams, useSearchParams} from "react-router";
import { toast } from "react-toastify";

import { Template } from "../../../../components/template/template.jsx";
import { Card } from "../../../../components/card/card.jsx";
import InputDateTime from "../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import Button from "../../../../components/button/button.jsx";

import aggridUtils from "../../../../utils/aggrid-utils.js";
import UserService from "../../../../services/web/user-service.js";
import ContactIdService from "../../../../services/web/contact-id-service.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import projectUtils from "../../../../utils/project-utils.js";
import ProjectUtils from "../../../../utils/project-utils.js";
import {FaPencil} from "react-icons/fa6";
import {Modal} from "../../../../components/modal/modal.jsx";
import InputForm from "../../../../components/input/input-form.jsx";

const initialFilter = {
    start_date: null,
    end_date: null,
    type: [],
    usernameAck: [{ value: "pendent", label: "Pendente" }],
};

const ContactIdEventsList = () => {
    const [searchParams] = useSearchParams();
    const navigate = navigateHook();

    const { id, mode } = useParams();
    const alarmSystemId = searchParams.get("alarmSystemId");
    const fireAlarm = String(mode).toLowerCase() === "fire-alarm";

    const gridRef = useRef(null);

    const [filters, setFilters] = useState(initialFilter);
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const [observationModalOpen, setObservationModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [observationValue, setObservationValue] = useState("");

    const openObservationModal = useCallback((event) => {
        setSelectedEvent(event);
        setObservationValue(event.observation ?? "");
        setObservationModalOpen(true);
    }, []);

    const closeObservationModal = () => {
        setObservationModalOpen(false);
        setSelectedEvent(null);
        setObservationValue("");
    }

    const { data: contactIdInfo } = useQuery({
        queryKey: ["contact-id-info", id],
        queryFn: () => ContactIdService.get(alarmSystemId).then((data) => data)
    });

    const { data: eventTypesResponse } = useQuery({
        queryKey: ["eventTypes", fireAlarm],
        queryFn: () =>
            fireAlarm
                ? ContactIdService.getFireAlarmEventTypes(id)
                : ContactIdService.getEventTypes(),
    });

    const eventTypeOptions = useMemo(() => {
        const list = eventTypesResponse?.data?.data;
        if (!Array.isArray(list)) return [];
        return list.map((item) => ({
            value: item.id,
            label: item.text,
        }));
    }, [eventTypesResponse]);

    const { data: usersAckResponse } = useQuery({
        queryKey: ["usersAck"],
        queryFn: () => UserService.getLoginUsers(),
    });

    const usersAckOptions = useMemo(() => {
        const list = usersAckResponse?.data;

        const fixedOptions = [
            { value: 'pendent', label: 'Pendente' },
            { value: 'SYSTEM', label: 'Sistema' },
        ];

        if (!Array.isArray(list)) return fixedOptions;

        const dbOptions = list.map((user) => ({
            value: user.id,
            label: user.text,
        }));

        return [...fixedOptions, ...dbOptions];
    }, [usersAckResponse]);

    const columnDefs = useMemo(() => {
        if (fireAlarm) {
            return [
                { headerName: "Data/Hora", field: "dt_event", sort: "desc", width: 180,
                    valueFormatter: ({ value }) =>
                        value ? moment(value).format("DD/MM/YYYY HH:mm") : "",
                },
                { headerName: "Evento", field: "data.message", flex: 1 },
                { headerName: "Zona | Loop | Dispositivo", field: "zone", flex: 1 },
            ];
        }

        return [
            { headerName: "Data/Hora", field: "dt_event", sort: "desc", width: 220,
                valueFormatter: ({ value }) =>
                    value ? moment(value).format("DD/MM/YYYY HH:mm") : "",
            },
            { headerName: "Evento", field: "data.message", flex: 1,
                cellRenderer: ({ value }) => {
                    if (value === "Offline") {
                        return <span className="text-warning font-bold">{value}</span>;
                    }
                    if (value === "Disparo De Alarme") {
                        return <span className="text-danger font-bold">{value}</span>;
                    }
                    return value;
                },
            },
            { headerName: "Setor / Usuário", flex: 1,
                valueGetter: ({ data }) => {
                    if (!data?.data) return "";
                    const { nameDescription, codUserZone } = data.data;

                    let result = nameDescription ?? "";
                    if (codUserZone > 0) {
                        result = result
                            ? `${result} - ${codUserZone}`
                            : String(codUserZone);
                    }
                    return result;
                },
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
            { headerName: "Encerrado por", field: "username_ack", width: 220 },
            { headerName: "Observação", field: "observation", flex: 1,
                cellRenderer: ({ value, data }) => (
                    <div className="flex h-full justify-between items-center-safe">
                        <span className="truncate flex-1" title={value ?? ""}>
                            {value ?? ""}
                        </span>
                        <Button
                            title="Editar observação"
                            className="!w-[30px] !h-[25px] !p-0"
                            onClick={() => openObservationModal(data)}>
                            <FaPen />
                        </Button>
                    </div>
                ),
                comparator: (a, b, nodeA, nodeB, isInverted) => {
                    return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
                }
            },
        ];
    }, [fireAlarm, openObservationModal]);

    const theme = useMemo(() => aggridUtils.generalTheme(40, 40), []);

    const validateDates = (f) => {
        const { start_date, end_date } = f;

        if (!start_date || !end_date) {
            projectUtils.swalErrorMessage("", "Data inicial ou final inválida.");
            return false;
        }

        return true;
    };

    const loadEvents = useCallback(async (customFilters, options = {}) => {
        const { skipValidation = false } = options;

        const f = customFilters ?? filters;

        if (!skipValidation && !validateDates(f)) return;

        setIsLoading(true);

        const payload = {
            id,
            ...(fireAlarm && { fireAlarm }),
            dtStart: f.start_date
                ? moment(f.start_date).format("DD/MM/YYYY HH:mm")
                : null,
            dtEnd: f.end_date
                ? moment(f.end_date).format("DD/MM/YYYY HH:mm")
                : null,
            type: Array.isArray(f.type) && f.type.length > 0
                ? f.type.map(t => t.value)
                : null,
            username_ack: Array.isArray(f.usernameAck) && f.usernameAck.length > 0
                ? f.usernameAck.map(u => u.value)
                : f.usernameAck?.value ?? [],
        };

        await ContactIdService.filterEvents(payload)
            .then((response) => {
                setRowData(response.data.data);
            })
            .catch((error) => {
                console.error(error);
                projectUtils.swalErrorMessage("", "Não foi possível carregar os dados.");
            })
            .finally(() => setIsLoading(false));
    }, [filters, id, fireAlarm]);

    const clearFilter = useCallback(() => {
        const resetFilter = { ...initialFilter };
        setFilters(resetFilter);
        setRowData([]);
        loadEvents(resetFilter, { skipValidation: true });
    }, [loadEvents]);

    const handlePrint = useCallback(() => {
        const dtStart = filters.start_date
            ? moment(filters.start_date).format("DD/MM/YYYY HH:mm")
            : null;

        const dtEnd = filters.end_date
            ? moment(filters.end_date).format("DD/MM/YYYY HH:mm")
            : null;

        const typeValue = Array.isArray(filters.type) && filters.type.length > 0
            ? filters.type.map(t => t.value).join(",")
            : null;

        const user = Array.isArray(filters.usernameAck) && filters.usernameAck.length > 0
            ? filters.usernameAck.map(u => u.value).join(",")
            : null;

        const searchParams = new URLSearchParams();

        if (dtStart) searchParams.set("dtinitial", dtStart);
        if (dtEnd) searchParams.set("dtfinal", dtEnd);
        if (typeValue) searchParams.set("type", typeValue);
        if (user) searchParams.set("user", user);
        if (fireAlarm) searchParams.set("fireAlarm", fireAlarm);

        const url = `/reports/contactid_events_info/?contactid=${id}&${searchParams.toString()}`;

        navigate(url, true);
    }, [filters, id, fireAlarm, navigate]);

    useEffect(() => {
        loadEvents(undefined, { skipValidation: true });
    }, [id, fireAlarm]);

    useEffect(() => {
        const handler = (e) => {
            if (e.key === "Enter") loadEvents();
            if (e.key === "Escape") clearFilter();
        };

        window.addEventListener("keyup", handler);
        return () => window.removeEventListener("keyup", handler);
    }, [loadEvents, clearFilter]);

    const saveObservation = async () => {
        if (!selectedEvent?.id) return;

        try {
            setIsLoading(true);

            await ContactIdService.updateEventObservation(
                selectedEvent.id,
                observationValue
            );

            ProjectUtils.success();

            closeObservationModal();

            loadEvents(undefined, { skipValidation: true });
        } catch (error) {
            console.error(error);
            ProjectUtils.swalErrorMessage("", "Não foi possìvel salvar.");
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
        <Template isLoading={isLoading}>
            <Template.Header className={"!h-[62px] flex items-center"}>
                <Template.Title className="flex flex-col justify-center items-start h-full" browserTitle={contactIdInfo?.description}>
                    <span>{contactIdInfo?.description}</span>

                    <span className="text-xs">
                        Endereço: {[contactIdInfo?.address, contactIdInfo?.number, contactIdInfo?.neighborhood, contactIdInfo?.city].filter(Boolean).join(", ")}
                        {" | "}
                        Contato: {contactIdInfo?.contact}
                    </span>
                </Template.Title>

                <Button title="Imprimir" disabled={rowData.length === 0} onClick={handlePrint}>
                    <FaPrint />
                </Button>
            </Template.Header>

            <Template.Body className={"mt-[62px]"}>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <InputDateTime
                                label="Data/hora inicial"
                                classNameWrapper="col-span-3"
                                selected={filters.start_date}
                                onChange={(v) => setFilters((p) => ({ ...p, start_date: v }))}
                            />

                            <InputDateTime
                                label="Data/hora final"
                                classNameWrapper="col-span-3"
                                selected={filters.end_date}
                                onChange={(v) =>
                                    setFilters((p) => ({ ...p, end_date: v }))
                                }
                            />

                            { !fireAlarm && (
                                <SelectComponent
                                    label="Tipo de evento"
                                    classNameWrapper="col-span-3"
                                    options={eventTypeOptions}
                                    value={filters.type}
                                    isMulti
                                    onChange={(v) => setFilters((p) => ({ ...p, type: v ?? [] }))}
                                />
                            )}

                            <div className={`flex gap-4 items-end ${fireAlarm ? "col-span-6" : "col-span-3"}`}>
                                { fireAlarm && (
                                    <SelectComponent
                                        label="Tipo de evento"
                                        classNameWrapper="flex-1"
                                        options={eventTypeOptions}
                                        value={filters.type}
                                        isMulti
                                        onChange={(v) => setFilters((p) => ({ ...p, type: v ?? [] }))}
                                    />
                                )}

                                { !fireAlarm && (
                                    <SelectComponent
                                        label="Encerrado por"
                                        classNameWrapper="flex-1"
                                        options={usersAckOptions}
                                        value={filters.usernameAck}
                                        isMulti
                                        isClearable
                                        onChange={(v) =>
                                            setFilters((p) => ({
                                                ...p,
                                                usernameAck: v ?? [],
                                            }))
                                        }
                                    />
                                )}

                                <div className={"flex gap-2"}>
                                    <Button target="primary-outline" onClick={clearFilter}>
                                        <FaEraser />
                                    </Button>

                                    <Button target="primary" onClick={() => loadEvents()} disabled={isLoading}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2 h-[calc(100vh-140px)]">
                    <Card.Body className="h-full">
                        <AgGridReact
                            ref={gridRef}
                            rowData={rowData}
                            columnDefs={columnDefs}
                            defaultColDef={{ sortable: true }}
                            suppressCellFocus
                            gridOptions={{ localeText: aggridUtils.localization() }}
                            animateRows={false}
                            theme={theme}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
            <Modal
                open={observationModalOpen}
                setOpen={setObservationModalOpen}
                title="Observação"
                className="w-4/12"
            >
                <Modal.Body>
                    <InputForm
                        id="observation"
                        label="Descrição"
                        value={observationValue}
                        onChange={(e) => setObservationValue(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer className="p-1">
                    <div className="w-full flex justify-end">
                        <Button onClick={saveObservation} actionFeedbackType='save'>

                            Salvar
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ContactIdEventsList;